import React from 'react';
import { toast as sonner } from 'sonner';
import { Bot, Sparkles, AlertCircle, AlertTriangle, Info, Loader2, X } from 'lucide-react';

// AI-native notification: reads like an agent reporting back, not an OS toast.
// Violet gradient avatar (the agent), a sparkle "Assistant"/agent eyebrow, the
// action, optional reasoning detail + a confidence meter, and a live pulse.
const KIND = {
  success: { grad: 'from-violet-600 to-fuchsia-600', Icon: Bot, live: true },
  agent: { grad: 'from-violet-600 to-fuchsia-600', Icon: Bot, live: true },
  info: { grad: 'from-sky-500 to-violet-500', Icon: Info, live: false },
  working: { grad: 'from-violet-600 to-fuchsia-600', Icon: Loader2, spin: true, live: false },
  warning: { grad: 'from-amber-500 to-orange-500', Icon: AlertTriangle, live: false },
  error: { grad: 'from-rose-500 to-orange-500', Icon: AlertCircle, live: false },
};

export default function AiToast({ id, kind = 'agent', message, description, agent, confidence }) {
  const k = KIND[kind] || KIND.agent;
  const Icon = k.Icon;
  const eyebrow = agent ? `${agent} Agent` : 'Assistant';
  return (
    <div className="relative w-[360px] max-w-[86vw] rounded-xl border border-violet-200/70 bg-white shadow-xl overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-violet-500 via-fuchsia-400 to-brand-500 opacity-80" />
      <div className="flex items-start gap-3 p-3.5 pt-4">
        <div className={`relative h-9 w-9 rounded-lg bg-gradient-to-br ${k.grad} text-white flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon className={`h-[18px] w-[18px] ${k.spin ? 'animate-spin' : ''}`} />
          {k.live && <span className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-violet-600">
            <Sparkles className="h-3 w-3" />{eyebrow}
          </div>
          <div className="text-sm text-slate-900 font-medium mt-0.5 leading-snug">{message}</div>
          {description && <div className="text-xs text-slate-500 mt-0.5 leading-snug">{description}</div>}
          {kind === 'working' && <div className="mt-1.5 text-[11px] text-violet-500 animate-pulse">working…</div>}
          {confidence != null && kind !== 'working' && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-slate-400">
              <span className="h-1 w-16 rounded-full bg-slate-200 overflow-hidden"><span className="block h-full bg-violet-500" style={{ width: `${Math.round(confidence * 100)}%` }} /></span>
              <span className="tnum">{Math.round(confidence * 100)}% confidence</span>
            </div>
          )}
        </div>
        <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => sonner.dismiss(id)} className="shrink-0 -mr-1 -mt-0.5 rounded-md p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label="Dismiss"><X className="h-4 w-4 pointer-events-none" /></button>
      </div>
    </div>
  );
}
